<template>
  <v-card class="pa-4">
    <h2>{{ reportTitle }}</h2>
    <v-row dense>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="selectedDept"
          :items="departments"
          item-text="name"
          item-value="id"
          label="Department"
          dense
          required
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="selectedClassification"
          :items="classificationOptions"
          item-text="subCode"
          item-value="id"
          label="File Classification"
          dense
          :disabled="!selectedDept"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-menu v-model="menuMonth" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedMonth"
              label="Month"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="selectedMonth"
            type="month"
            @change="menuMonth = false"
          />
        </v-menu>
      </v-col>
      <v-col cols="12" md="3" class="d-flex align-end">
        <v-btn
          color="primary"
          @click="generateReport"
          :disabled="!selectedDept || !selectedMonth"
        >
          Generate Report
        </v-btn>
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <div v-if="reportData.length">
      <!-- Title and subtitle rows -->
      <div class="mb-2">
        <div
          style="
            background: #92d050;
            font-family: Tahoma;
            font-size: 1.1rem;
            font-weight: bold;
            text-align: center;
            padding: 8px;
            border-radius: 4px;
          "
        >
          {{ reportTitle }}
        </div>
        <div
          style="
            background: #ededed;
            font-family: Tahoma;
            font-size: 0.95rem;
            text-align: center;
            padding: 6px;
            border-radius: 4px;
          "
        >
          Closed Files by Branch,
          <span style="font-weight: bold">
            {{
              selectedDept === "all" || !selectedDept
                ? "All Departments"
                : departments.find((d) => d.id === selectedDept)?.name ||
                  "Department"
            }}
          </span>
          ,
          <span style="font-weight: bold">
            {{
              selectedClassification === "all" || !selectedClassification
                ? "All Classifications"
                : classificationOptions.find(
                    (c) => c.id === selectedClassification
                  )?.subCode || "Classification"
            }}
          </span>
        </div>
        <div
          style="
            background: #fffbe6;
            color: #856404;
            font-family: Tahoma;
            font-size: 0.95rem;
            text-align: center;
            padding: 6px;
            border-radius: 4px;
            margin-top: 6px;
          "
        >
          For detailed breakdown by branch and classification, please click
          <strong>Export to Excel</strong>.
        </div>
      </div>
      <v-btn
        color="success"
        class="mb-4"
        @click="exportExcel"
        :loading="exporting"
      >
        <v-icon left>mdi-file-excel</v-icon>
        Export to Excel
      </v-btn>
      <v-simple-table>
        <thead>
          <tr style="background: #ededed">
            <th
              style="
                font-family: Tahoma;
                font-size: 13px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                border: 1px solid #bbb;
              "
            >
              Branch
            </th>
            <th
              style="
                font-family: Tahoma;
                font-size: 13px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                border: 1px solid #bbb;
              "
            >
              Below 30 Days
            </th>
            <th
              style="
                font-family: Tahoma;
                font-size: 13px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                border: 1px solid #bbb;
              "
            >
              Above 30 Days
            </th>
            <th
              style="
                font-family: Tahoma;
                font-size: 13px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                border: 1px solid #bbb;
              "
            >
              Above 45 Days
            </th>
            <th
              style="
                font-family: Tahoma;
                font-size: 13px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                border: 1px solid #bbb;
              "
            >
              Above 60 Days
            </th>
            <th
              style="
                font-family: Tahoma;
                font-size: 13px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                border: 1px solid #bbb;
              "
            >
              Above 90 Days
            </th>
            <th
              style="
                font-family: Tahoma;
                font-size: 13px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                border: 1px solid #bbb;
              "
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in reportData"
            :key="row.branch"
            style="
              font-family: Tahoma;
              font-size: 13px;
              background: #fff;
              border: 1px solid #bbb;
            "
          >
            <td style="text-align: left">{{ row.branch }}</td>
            <td style="text-align: center; font-weight: bold">
              {{ row.below30 }}
            </td>
            <td style="text-align: center; font-weight: bold">
              {{ row.above30 }}
            </td>
            <td style="text-align: center; font-weight: bold">
              {{ row.above45 }}
            </td>
            <td style="text-align: center; font-weight: bold">
              {{ row.above60 }}
            </td>
            <td style="text-align: center; font-weight: bold">
              {{ row.above90 }}
            </td>
            <td style="text-align: center; font-weight: bold">
              {{ row.total }}
            </td>
          </tr>
          <tr
            style="
              font-family: Tahoma;
              font-size: 13px;
              font-weight: bold;
              background: #ededed;
              border: 1px solid #bbb;
            "
          >
            <td style="text-align: left"><strong>Total</strong></td>
            <td style="text-align: center">
              <strong>{{ totals.below30 }}</strong>
            </td>
            <td style="text-align: center">
              <strong>{{ totals.above30 }}</strong>
            </td>
            <td style="text-align: center">
              <strong>{{ totals.above45 }}</strong>
            </td>
            <td style="text-align: center">
              <strong>{{ totals.above60 }}</strong>
            </td>
            <td style="text-align: center">
              <strong>{{ totals.above90 }}</strong>
            </td>
            <td style="text-align: center">
              <strong>{{ totals.total }}</strong>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <div
        class="mt-2"
        style="font-family: Tahoma; font-size: 1rem; font-weight: bold"
      >
        Grand Total: {{ totals.total }}
      </div>
    </div>
    <v-alert v-else-if="noDataMessage" type="warning" outlined class="mt-4">
      {{ noDataMessage }}
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-4">
      Please select filters and click "Generate Report" to view data.
    </v-alert>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { apiConfig } from "../../config/api.config";

export default {
  name: "ClosedFilesReport",
  data() {
    return {
      selectedDept: null,
      selectedClassification: null,
      selectedMonth: null,
      menuMonth: false,
      departments: [],
      classificationOptions: [],
      reportData: [],
      totals: {
        below30: 0,
        above30: 0,
        above45: 0,
        above60: 0,
        above90: 0,
        total: 0,
      },
      exporting: false,
      noDataMessage: "",
    };
  },
  computed: {
    reportTitle() {
      if (!this.selectedDept || !this.selectedMonth)
        return "Closed Files Report";
      const deptName =
        this.selectedDept === "all"
          ? "All Departments"
          : this.departments.find((d) => d.id === this.selectedDept)?.name ||
            "";
      const monthStr = moment(this.selectedMonth).format("MMMM YYYY");
      return `${deptName} CLOSED FILES - ${monthStr}`;
    },
  },
  watch: {
    selectedDept(val) {
      this.loadClassifications(val);
    },
  },
  methods: {
    async loadDepartments() {
      const res = await axios.get(`${apiConfig.baseURL}/depts`);
      // Sort departments by name and add "All Departments" option
      this.departments = [
        { id: "all", name: "All Departments" },
        ...res.data.sort((a, b) => a.name.localeCompare(b.name)),
      ];
    },
    async loadClassifications(deptId) {
      if (!deptId || deptId === "all") {
        // Only show "All Classifications" when no department or "All Departments" is selected
        this.classificationOptions = [
          { id: "all", subCode: "All Classifications" },
        ];
        return;
      }
      // Fetch only classifications relevant to the selected department
      const res = await axios.get(
        `${apiConfig.baseURL}/subDepts?deptId=${deptId}`
      );
      // Sort and set options
      this.classificationOptions = [
        { id: "all", subCode: "All Classifications" },
        ...res.data
          .filter((c) => c.deptId === deptId) // Ensure only relevant classifications
          .sort((a, b) => a.subCode.localeCompare(b.subCode)),
      ];
    },
    async generateReport() {
      this.reportData = [];
      this.totals = {
        below30: 0,
        above30: 0,
        above45: 0,
        above60: 0,
        above90: 0,
        total: 0,
      };
      this.noDataMessage = "";
      try {
        const params = {
          deptId: this.selectedDept === "all" ? null : this.selectedDept,
          classificationId:
            this.selectedClassification === "all"
              ? null
              : this.selectedClassification,
          month: moment(this.selectedMonth).format("YYYY-MM"),
        };
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/closed-files`,
          { params }
        );
        this.processReportData(res.data);
        // Show alert if no data
        if (
          !Array.isArray(res.data.reportData) ||
          res.data.reportData.length === 0
        ) {
          const deptName =
            this.departments.find((d) => d.id === this.selectedDept)?.name ||
            "selected department";
          const className =
            this.selectedClassification === "all"
              ? "all classifications"
              : this.classificationOptions.find(
                  (c) => c.id === this.selectedClassification
                )?.subCode || "selected classification";
          const monthStr = moment(this.selectedMonth).format("MMMM YYYY");
          this.noDataMessage = `No closed files found for ${deptName} and ${className} in ${monthStr}.`;
        }
      } catch (err) {
        this.noDataMessage =
          err.message || "Failed to fetch report data. Please try again.";
      }
    },
    processReportData(data) {
      // Accepts { reportData, totals } from backend
      this.reportData = Array.isArray(data.reportData) ? data.reportData : [];
      this.totals = data.totals || {
        below30: 0,
        above30: 0,
        above45: 0,
        above60: 0,
        above90: 0,
        total: 0,
      };
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = {
          deptId: this.selectedDept === "all" ? "" : this.selectedDept,
          classificationId:
            this.selectedClassification === "all"
              ? ""
              : this.selectedClassification,
          month: moment(this.selectedMonth).format("YYYY-MM"),
        };
        const url =
          `${apiConfig.baseURL}/compliance/closed-files/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
        // Optionally show a v-alert or toast here
      } finally {
        this.exporting = false;
      }
    },
  },
  mounted() {
    this.loadDepartments();
  },
};
</script>
